import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { eatDayOf, formatEatDateTime, formatIsoDay } from '@wodi/domain';
import {
  Button,
  DateField,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Section,
  Select,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import {
  bookableProceduresQuery,
  bookCase,
  surgeonsQuery,
  theatreListQuery,
} from '../api/theatre.ts';
import { visitQuery } from '../api/visits.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ShortcutHint } from '../layout/screen.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { toasts } from '../toasts.ts';
import { priceWords, TheatreItemPicker, type PickedItem } from './item-picker.tsx';
import styles from './theatre.module.css';
import { errorText, URGENCY_WORDS } from './words.ts';

// Book a theatre case from the patient's open visit or stay (docs/plans/phase2-theatre.md §6).
// The procedure is one already requested on the visit, or one picked from the catalogue,
// which places the request (and its charge) with the booking.

const route = getRouteApi('/_app/visits/$visitId/theatre-booking');

const NEW_PROCEDURE = 'new';
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** The EAT wall-clock date and time as an instant (Kenya has no daylight saving). */
function eatInstant(day: string, time: string): string {
  return new Date(`${day}T${time}:00+03:00`).toISOString();
}

export function BookCasePage() {
  const { visitId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const visit = useQuery(visitQuery(visitId));
  const bookable = useQuery(bookableProceduresQuery(visitId));
  const list = useQuery(theatreListQuery(null));
  const surgeons = useQuery(surgeonsQuery);
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const procedureRef = useRef<HTMLDivElement>(null);
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [procedure, setProcedure] = useState<string | null>(null);
  const [item, setItem] = useState<PickedItem | null>(null);
  const [indication, setIndication] = useState('');
  const [theatreId, setTheatreId] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(formatIsoDay(eatDayOf(new Date())));
  const [time, setTime] = useState('');
  const [minutes, setMinutes] = useState('60');
  const [surgeonId, setSurgeonId] = useState<string | null>(null);
  const [urgency, setUrgency] = useState<'elective' | 'emergency'>('elective');
  const [bookingNote, setBookingNote] = useState('');
  const mayBook = session !== null && can(session, 'theatre.book');

  const choice = procedure ?? (bookable.data?.length === 0 ? NEW_PROCEDURE : null);
  const minutesValue = Number(minutes);
  const minutesOk = Number.isInteger(minutesValue) && minutesValue >= 1 && minutesValue <= 1440;
  const book = useMutation({
    mutationFn: () =>
      bookCase({
        clientRequestId,
        visitId,
        theatreId: theatreId ?? '',
        scheduledStart: eatInstant(day ?? '', time),
        expectedMinutes: minutesValue,
        surgeonUserId: surgeonId ?? '',
        urgency,
        ...(choice === NEW_PROCEDURE
          ? {
              serviceItemId: item?.serviceItemId ?? '',
              ...(indication.trim() === '' ? {} : { indication: indication.trim() }),
            }
          : { serviceOrderId: choice ?? '' }),
        ...(bookingNote.trim() === '' ? {} : { bookingNote: bookingNote.trim() }),
      }),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: ['theatre'] });
      toasts.show({
        title: 'Case booked',
        description: `${c.theatre.name}, ${formatEatDateTime(new Date(c.scheduledStart))}`,
      });
      await navigate({ to: '/theatre/cases/$caseId', params: { caseId: c.id } });
    },
  });
  const ready =
    online &&
    mayBook &&
    choice !== null &&
    (choice !== NEW_PROCEDURE || item !== null) &&
    theatreId !== null &&
    day !== null &&
    TIME.test(time) &&
    minutesOk &&
    surgeonId !== null &&
    !book.isPending;
  useShortcut(
    'b',
    () => {
      procedureRef.current?.querySelector<HTMLElement>('input')?.focus();
    },
    { enabled: mayBook, description: 'Choose the procedure' },
  );
  useShortcut(
    'mod+enter',
    () => {
      if (ready) book.mutate();
    },
    { allowInFields: true, enabled: mayBook, description: 'Book case' },
  );

  if (visit.isPending) return <LoadingBlock label="Loading the visit" />;
  if (visit.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void visit.refetch()}>Try again</Button>}>
        {errorText(visit.error, "The visit couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const theatres = list.data?.theatres ?? [];
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={visit.data.patientId} />
      <PageHeader
        title="Book theatre case"
        description={`On visit ${visit.data.visitNumber}. The case, its checklist and its charges belong to this visit.`}
      />
      {!mayBook ? (
        <Notice tone="info">
          You don't have permission to book theatre cases. Ask a clinician to book.
        </Notice>
      ) : visit.data.status !== 'open' ? (
        <Notice tone="info">
          This visit is closed or cancelled. Book the case on the visit or stay the patient is on
          now.
        </Notice>
      ) : (
        <Section title="Case">
          <form
            className={styles.form}
            aria-label="Book theatre case"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready) book.mutate();
            }}
          >
            <div ref={procedureRef} className={styles.form}>
              {bookable.isError ? (
                <Notice tone="error">
                  {errorText(bookable.error, "The visit's procedure requests couldn't be loaded.")}
                </Notice>
              ) : bookable.data !== undefined && bookable.data.length > 0 ? (
                <RadioGroup
                  label="Procedure"
                  value={choice ?? ''}
                  onChange={setProcedure}
                  options={[
                    ...bookable.data.map((p) => ({
                      id: p.serviceOrderId,
                      label: `${p.code} ${p.name}`,
                      description: `Requested ${formatEatDateTime(new Date(p.orderedAt))}`,
                    })),
                    { id: NEW_PROCEDURE, label: 'Another procedure from the catalogue' },
                  ]}
                />
              ) : null}
              {choice === NEW_PROCEDURE ? (
                item === null ? (
                  <TheatreItemPicker
                    label="Procedure from the catalogue"
                    visitId={visitId}
                    purpose="procedure"
                    onSelect={setItem}
                  />
                ) : (
                  <p className={styles.itemTitle}>
                    <span className={`wodi-id ${styles.code}`}>{item.code}</span>
                    {`${item.name}, ${priceWords(item.price)} `}
                    <Button
                      dense
                      variant="quiet"
                      onPress={() => {
                        setItem(null);
                      }}
                    >
                      Change
                    </Button>
                  </p>
                )
              ) : null}
              {choice === NEW_PROCEDURE ? (
                <TextArea
                  label="Indication (optional)"
                  description="Why the procedure is needed. Kept with the request."
                  value={indication}
                  onChange={setIndication}
                  rows={2}
                />
              ) : null}
            </div>
            <div className={styles.row}>
              <Select
                label="Theatre"
                placeholder={theatres.length === 0 ? 'No theatres set up' : 'Choose one'}
                options={theatres.map((t) => ({ id: t.id, label: t.name }))}
                value={theatreId}
                onChange={(v) => {
                  setTheatreId(typeof v === 'string' ? v : null);
                }}
              />
              <Select
                label="Surgeon"
                placeholder="Choose one"
                options={(surgeons.data ?? []).map((s) => ({ id: s.id, label: s.displayName }))}
                value={surgeonId}
                onChange={(v) => {
                  setSurgeonId(typeof v === 'string' ? v : null);
                }}
              />
            </div>
            <div className={styles.row}>
              <DateField label="Date" value={day} onChange={setDay} />
              <TextField
                label="Start time"
                description="24-hour, like 14:05"
                value={time}
                onChange={setTime}
                width="sm"
                {...(time !== '' && !TIME.test(time)
                  ? { errorMessage: 'Write the time as hours and minutes, like 14:05.' }
                  : {})}
              />
              <TextField
                label="Expected length"
                unit="min"
                inputMode="numeric"
                value={minutes}
                onChange={setMinutes}
                width="sm"
                {...(minutes !== '' && !minutesOk
                  ? { errorMessage: 'Between 1 and 1,440 minutes.' }
                  : {})}
              />
            </div>
            <RadioGroup
              label="Urgency"
              orientation="horizontal"
              value={urgency}
              onChange={(v) => {
                setUrgency(v === 'emergency' ? 'emergency' : 'elective');
              }}
              options={[
                { id: 'elective', label: URGENCY_WORDS.elective },
                { id: 'emergency', label: URGENCY_WORDS.emergency },
              ]}
            />
            <TextArea
              label="Booking note (optional)"
              description="For the theatre team, like equipment needed."
              value={bookingNote}
              onChange={setBookingNote}
              rows={2}
            />
            {!online ? (
              <Notice tone="info">
                No connection to Wodi. Booking waits for the connection; nothing is saved on this
                device.
              </Notice>
            ) : null}
            {book.error === null ? null : (
              <Notice tone="error">
                {errorText(book.error, "The case wasn't booked. Try again.")}
              </Notice>
            )}
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                isDisabled={!ready}
                isPending={book.isPending}
                pendingLabel="Booking…"
              >
                Book case
              </Button>
              <ShortcutHint combo="mod+enter" action="book" />
            </div>
          </form>
        </Section>
      )}
    </div>
  );
}
